import { ref, type Ref } from 'vue'
import axiosInstance from '../plugins/axios'
import { useSessionStore } from '../store/sessionStore'
import { useModuleStore } from '../store/moduleStore'
import { useThemeStore } from '../store/themeStore'
import { useRouter } from 'vue-router'

export function useAuth() {
  const router = useRouter()
  const isAuthenticated = ref(false)

  const checkIfAuthenticated = async () => {
    if (!localStorage.getItem('token')) {
      isAuthenticated.value = false
      return
    }

    try {
      const res = await axiosInstance.get('/user/isAuthenticated')
      if (!res.data) {
        localStorage.removeItem('token')
        isAuthenticated.value = false
        return
      }
      isAuthenticated.value = res.data
    } catch (error: any) {
      console.error('Error loading user data', error.response ? error.response.data : error)
      isAuthenticated.value = false
    }
  }

  const signIn = async (credentials: { username: string; password: string }) => {
    try {
      const res = await axiosInstance.post('/user/signIn', credentials)
      localStorage.setItem('token', res.data.token)
      isAuthenticated.value = true
      router.push('/')
    } catch (error: any) {
      console.error('Error signing in', error.response ? error.response.data : error)
      isAuthenticated.value = false
    }
  }

  const signOut = async () => {
    const sessionStore = useSessionStore()
    const moduleStore = useModuleStore()
    const themeStore = useThemeStore()

    await axiosInstance.post('/auth/logout')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('tenant')
    sessionStore.setToken(null)
    sessionStore.setUser(null)
    sessionStore.setTenant(null)
    moduleStore.reset()
    themeStore.reset(false)
    isAuthenticated.value = false
    router.push('/login')
  }

  return {
    isAuthenticated,
    checkIfAuthenticated,
    signIn,
    signOut,
    checkSessionStore: () => checkSessionStore(isAuthenticated)
  }
}

export async function checkSessionStore(isAuthenticated: Ref<boolean>) {
  const sessionStore = useSessionStore()

  const token = localStorage.getItem('token')
  if (token) {
    try {
      const res: any = await axiosInstance.get('/auth/user', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      const { user, tenant } = res.data
      if (user) {
        sessionStore.setToken(token)
        sessionStore.setUser(user)
        sessionStore.setTenant(tenant)
        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem('tenant', JSON.stringify(tenant))
      }
      isAuthenticated.value = true
    } catch (error) {
      console.error('Error fetching session', error)
    }
  }
}
